﻿/*
 * Purpose: Compose phone number input with country selector and validation label.
 * Boundary: Layout only; no local state or effects; fully controlled externally.
 * Invariants:
 * - Displays country selector and phone input side by side.
 * - Shows validation label only if enabled and input present.
 * - Flags load from /assets/flags/{flag}.svg.
 * - ASCII only; no Unicode in comments or strings.
 */

// Imports
import { CountrySelector, PhoneInput } from './phonenumber-controls';

// Component PhoneNumberLayout:
// Stateless layout composing selector, input, and validation label
// Receives all state and handlers from usePhoneNumber hook
export default function PhoneNumberLayout({
    enableCountrySelector,
    showValidationLabel,
    selectedCountry,
    phoneNumber,
    setPhoneNumber,
    isDropdownOpen,
    onToggleDropdown,
    onCountrySelect,
    isValid,
    filteredCountries,
    searchTerm,
    setSearchTerm,
    dropdownRef,
}) {
    // Rendering - country selector, input, and validation label
    return (
        <div className="w-full space-y-6">
            {/* Label group - wraps input row and caption */}
            <label className="text-sm md:text-sm text-neutral-300">
                <span className="text-sm md:text-sm text-neutral-300">Cell number</span>

                {/* Input row - selector and phone field */}
                <div className="flex flex-nowrap items-center gap-1.5 md:gap-2">
                    <CountrySelector
                        selectedCountry={selectedCountry}
                        enableCountrySelector={enableCountrySelector}
                        isDropdownOpen={isDropdownOpen}
                        onToggleDropdown={onToggleDropdown}
                        filteredCountries={filteredCountries}
                        onCountrySelect={onCountrySelect}
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        dropdownRef={dropdownRef}
                    />
                    <PhoneInput
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        disabled={!selectedCountry}
                    />
                </div>
            </label>

            {/* Validation label - conditional display based on flags */}
            {showValidationLabel && phoneNumber && (
                <p className={`mt-2 text-sm ${isValid ? 'text-emerald-600' : 'text-red-600'}`}>
                    {isValid
                        ? `Valid ${selectedCountry?.name} number`
                        : `Please enter a valid ${selectedCountry?.name} number`}
                </p>
            )}
        </div>
    );
}